import {resolveEvaluationSelection} from './evaluationSelection';
import {datasetImportKey} from './datasetImportIdentity';
import { authFetch } from "./adminAuth";
import type { BadCaseAudioPage } from "./types";
import type {
  AnalysisResponse,
  AsrCapabilities,
  Candidate,
  CaseFilters,
  PlatformReviewCandidate,
  ReviewFindingSummary,
  ReviewAudioFilters,
  ReviewAudioGroup,
  Dataset,
  DatasetCaseSnapshot,
  EvaluationProfileVersion,
  GroupedResponse,
  MetricDefinitionVersion,
  PlanRequest,
  PlatformBadCase,
  PlatformBadCaseDetail,
  PlatformReviewClaim,
  PlatformReviewQueueStats,
  QualityGatePolicyVersion,
  ReferenceBoundaryCapabilities,
  ReferenceBoundaryCatalog,
  ReferenceBoundaryAdmission,
  ReferenceBoundaryDryRunRequest,
  ReferenceBoundaryDryRunResponse,
  RegressionSet,
  RegressionSetVersion,
  Run,
  RunDetail,
  RunProgressSnapshot,
  RunMetricResponse,
  RunQualityGateResponse,
  RunComparison,
  RunTaskRecord,
  TestSuite,
  Voice,
  VoiceCatalogResponse,
} from "./types";
import { caseQuery } from "./casePresentation";

export const API_BASE = import.meta.env.VITE_API_BASE ?? "";
const PLATFORM_BASE = "/api/v1/platform";

export function assetUrl(path: string) {
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE}${path}`;
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

export async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await authFetch(`${API_BASE}${path}`, {
    ...options,
    headers: { Accept: "application/json", ...options?.headers },
  });
  if (!response.ok) {
    const body = await response.text();
    throw new ApiError(body || `HTTP ${response.status}`, response.status);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

interface Page<T> {
  total: number;
  offset: number;
  limit: number;
  items: T[];
}

interface PlatformDataset {
  id: string;
  key: string;
  name: string;
  description: string;
  status: string;
  created_by: string | null;
  created_at: string;
  version_count: number;
  latest_version_id: string | null;
}

interface PlatformDatasetVersion {
  id: string;
  dataset_id: string;
  parent_version_id: string | null;
  version_number: number;
  version_label: string;
  schema_version: string;
  original_name: string;
  status: string;
  summary: Record<string, unknown>;
  validation: Record<string, unknown>;
  content_sha256: string | null;
  manifest_sha256: string | null;
  case_count: number;
  enabled_case_count: number;
  created_by: string | null;
  created_at: string;
  sealed_at: string | null;
}

interface PlatformRun {
  id: string;
  dataset_version_id: string;
  run_type: string;
  status: string;
  version: number;
  planned_requests: number;
  plan_sha256: string | null;
  config: Record<string, unknown>;
  voice_catalog_snapshot?: Record<string, unknown>;
  detector_version: string | null;
  quality_policy_version: string | null;
  created_by: string | null;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

const dictionary = (value: unknown): Record<string, number> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).flatMap(([key, item]) =>
    typeof item === "number" ? [[key, item]] : []));
};

function mapDataset(version: PlatformDatasetVersion): Dataset {
  const summary = version.summary ?? {};
  return {
    id: version.id,
    dataset_id: version.dataset_id,
    original_name: version.version_label || version.original_name,
    source_filename: version.original_name,
    version_label: version.version_label,
    status: version.status,
    created_at: version.created_at,
    content_sha256: version.content_sha256 ?? undefined,
    manifest_sha256: version.manifest_sha256 ?? undefined,
    sealed_at: version.sealed_at ?? undefined,
    summary: {
      enabled_cases: version.enabled_case_count,
      scenes: dictionary(summary.scenes),
      expected_styles: dictionary(summary.expected_styles),
      languages: dictionary(summary.languages),
    },
  };
}

function emptySummary(total: number, status: string) {
  const terminal = ["completed", "failed", "cancelled", "stopped", "blocked_billing"].includes(status);
  return {
    total,
    success: status === "completed" ? total : 0,
    failed: status === "failed" ? total : 0,
    remaining: terminal ? 0 : total,
    progress: terminal ? 1 : 0,
  };
}

function mapRun(run: PlatformRun): Run {
  return {
    id: run.id,
    status: run.status,
    run_type: run.run_type,
    config: { ...run.config, dataset_id: run.dataset_version_id },
    summary: emptySummary(run.planned_requests, run.status),
    created_at: run.created_at,
    updated_at: run.updated_at,
    version: run.version,
    planned_requests: run.planned_requests,
    plan_sha256: run.plan_sha256,
    dataset_version_id: run.dataset_version_id,
    created_by: run.created_by,
    approved_by: run.approved_by,
    approved_at: run.approved_at,
    voice_catalog_snapshot: run.voice_catalog_snapshot,
  };
}

function summaryFromProgress(progress: RunProgressSnapshot) {
  const success = (progress.tasks.statuses.succeeded ?? 0) + (progress.tasks.statuses.analyzed ?? 0);
  const failed = (progress.tasks.statuses.definitive_failed ?? 0) + (progress.tasks.statuses.ambiguous_billed ?? 0);
  const cancelled = progress.tasks.statuses.cancelled ?? 0;
  const finished = Math.min(progress.planned_requests, success + failed + cancelled);
  return {
    total: progress.planned_requests,
    success,
    failed,
    remaining: Math.max(0, progress.planned_requests - finished),
    progress: progress.planned_requests ? finished / progress.planned_requests : 0,
  };
}

const safeId = (value: string, maximum = 72) =>
  value.trim().replace(/[^A-Za-z0-9_.-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, maximum) || "item";

export function expandPlatformTasks(body: PlanRequest, cases: DatasetCaseSnapshot[]) {
  if (body.assignment_mode === "paired" && cases.length !== body.voice_ids.length) {
    throw new Error(`一一配对要求 Case 数与声线数相同（当前 ${cases.length} / ${body.voice_ids.length}）`);
  }
  const pairs: Array<[DatasetCaseSnapshot, string]> = [];
  if (body.assignment_mode === "cartesian") {
    for (const item of cases) for (const voiceId of body.voice_ids) pairs.push([item, voiceId]);
  } else if (body.assignment_mode === "paired") {
    cases.forEach((item, index) => pairs.push([item, body.voice_ids[index]]));
  } else {
    cases.forEach((item, index) => pairs.push([item, body.voice_ids[index % body.voice_ids.length]]));
  }
  const tasks: Array<Record<string, unknown>> = [];
  for (const [item, voiceId] of pairs) {
    for (let repeatIndex = 1; repeatIndex <= body.repeats; repeatIndex += 1) {
      const ordinal = tasks.length + 1;
      tasks.push({
        task_id: `${safeId(item.benchmark_case_id, 80)}__${safeId(voiceId, 80)}__r${repeatIndex}__${ordinal}`.slice(0, 255),
        request_id: `${safeId(body.run_id, 180)}-request-${ordinal}`,
        benchmark_case_id: item.benchmark_case_id,
        voice_id: voiceId,
        text: item.input_text,
        expected_spoken_text: item.expected_spoken_text,
        expected_style: item.expected_style || null,
        scene: item.scene || null,
        language: item.language || null,
        repeat_index: repeatIndex,
        speed: body.speed,
        audio_format: body.audio_format,
        metadata: item.split ? { split: item.split } : {},
      });
    }
  }
  if (tasks.length > 10_000) throw new Error("单个 Run 最多 10,000 个任务，请拆分数据集或声线");
  return tasks;
}

export const api = {
  resultAudio: (runId:string,offset=0,filters?:import('./components/ResultAudioList').AudioResultFilters,anchorId?:string) => {
    const query=new URLSearchParams({limit:anchorId?'1':'20',offset:String(offset),...filters});if(anchorId)query.set('anchor_id',anchorId);
    return request<{total:number;items:import('./components/ResultAudioList').AudioResultRow[];voices:string[];voice_options?:{value:string;label:string}[];repeats:string[];previous_id:string|null;next_id:string|null}>(`${PLATFORM_BASE}/runs/${encodeURIComponent(runId)}/audio-results?${query}`);
  },
  voiceReference: (runId:string,attemptId:string,referenceId?:string,refresh=false) => {
    const params=new URLSearchParams();if(referenceId)params.set('reference_id',referenceId);if(refresh)params.set('refresh','true');
    return request<{status:'available'|'loading'|'missing'|'error';reference_id?:string;audio_url?:string;reason?:string;duration_seconds?:number;checked_at?:number}>(`${PLATFORM_BASE}/runs/${encodeURIComponent(runId)}/audio-results/${encodeURIComponent(attemptId)}/voice-reference?${params}`);
  },
  resultAudioDetail: (runId:string,attemptId:string) => request<import('./components/ResultAudioList').AudioResultDetail>(`${PLATFORM_BASE}/runs/${encodeURIComponent(runId)}/audio-results/${encodeURIComponent(attemptId)}`),
  saveResultAudioReview: (runId:string,attemptId:string,body:unknown) => request<import('./components/ResultAudioList').AudioResultDetail['reviews'][number]>(`${PLATFORM_BASE}/runs/${encodeURIComponent(runId)}/audio-results/${encodeURIComponent(attemptId)}/reviews`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}),
  systemInfo: () => request<{ commit: string | null; deployed_at: string | null; pause_policy_version: string; clause_pause_threshold_seconds: number; interior_pause_threshold_seconds: number;
    audio_detector_version?: string;
    transient_review?: { version: string; speech_jump_threshold: number; speech_score_minimum: number; rule_score_threshold: number };
    broadband_review?: { version: string; short_texture_seconds: number };
    tonal_review?: { version: string; quiet_max_dbfs: number; quiet_max_seconds: number; quiet_relative_db: number; speech_prominence_db: number; relative_review_db?: number };
  }>(`${PLATFORM_BASE}/system-info`),
  datasets: async (): Promise<Dataset[]> => {
    const versions:PlatformDatasetVersion[]=[];
    let expectedTotal:number|undefined;
    for(let offset=0;;){
      const page=await request<Page<PlatformDatasetVersion>>(`${PLATFORM_BASE}/datasets/version-summaries?limit=500&offset=${offset}`);
      if(expectedTotal!==undefined&&expectedTotal!==page.total)throw new Error('数据集目录已更新，请刷新重试');
      expectedTotal=page.total;versions.push(...page.items);offset+=page.items.length;
      if(offset>=page.total)break;
      if(!page.items.length)throw new Error('数据集版本列表读取不完整，请重试');
    }
    if(new Set(versions.map(v=>v.id)).size!==versions.length)throw new Error('数据集目录已更新，请刷新重试');
    return versions.map(mapDataset);
  },
  downloadDatasetSource: async (versionId: string) => {
    const response = await fetch(`${API_BASE}${PLATFORM_BASE}/dataset-versions/${encodeURIComponent(versionId)}/download`);
    if (!response.ok) throw new ApiError("数据集下载失败，请确认管理员登录状态及原文件是否存在", response.status);
    return response.blob();
  },
  downloadDatasetTemplate: async () => {
    const response = await fetch(`${API_BASE}${PLATFORM_BASE}/dataset-template`, {
      headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
    });
    if (!response.ok) throw new ApiError(await response.text() || `HTTP ${response.status}`, response.status);
    return response.blob();
  },
  uploadDataset: async (file: File, project: {project_id?:string} = {}) => {
    const stem = file.name.replace(/\.[^.]+$/, "");
    const existing: Array<{key:string;name:string;description:string}>=[];
    for(let offset=0;;offset+=500){
      const page=await request<Page<{key:string;name:string;description:string}>>(`${PLATFORM_BASE}/datasets?limit=500&offset=${offset}`);
      existing.push(...page.items);if(offset+page.items.length>=page.total||!page.items.length)break;
    }
    const key = await datasetImportKey(stem,project.project_id,existing);
    const description=existing.find(item=>item.key===key&&item.name===stem)?.description??"";
    const dataset = await request<PlatformDataset>(`${PLATFORM_BASE}/datasets`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key, name: stem || key, description, ...project }),
    }).catch(datasetImportError);
    const data = new FormData();
    const timestamp = new Date().toISOString().replace(/\D/g, "").slice(0, 17);
    data.append("version_label", `v${timestamp}`);
    data.append("schema_version", "benchmark-xlsx-v1");
    data.append("file", file);
    const version = await request<PlatformDatasetVersion>(
      `${PLATFORM_BASE}/datasets/${encodeURIComponent(dataset.id)}/versions/import`,
      { method: "POST", body: data },
    );
    return { id: version.id };
  },
  datasetCases: (datasetId: string, versionId: string) =>
    request<Page<DatasetCaseSnapshot>>(
      `${PLATFORM_BASE}/datasets/${encodeURIComponent(datasetId)}/versions/${encodeURIComponent(versionId)}/cases?limit=10000`,
    ),
  datasetDiff: (datasetId: string, left: string, right: string) => request<{ added: string[]; removed: string[]; changed: string[]; unchanged: string[] }>(
    `${PLATFORM_BASE}/datasets/${encodeURIComponent(datasetId)}/versions/diff?${new URLSearchParams({ left_version_id: left, right_version_id: right })}`,
  ),
  voices: (provider: string, refresh = false) =>
    request<VoiceCatalogResponse>(
      `${PLATFORM_BASE}/voice-catalogs/${encodeURIComponent(provider)}${refresh ? "?refresh=true" : ""}`,
    ),
  asrCapabilities: () => request<AsrCapabilities>(`${PLATFORM_BASE}/asr/capabilities`),
  metrics: () => request<{ total: number; items: MetricDefinitionVersion[] }>(`${PLATFORM_BASE}/metrics`),
  evaluationProfiles: () => request<{ total: number; items: EvaluationProfileVersion[] }>(`${PLATFORM_BASE}/evaluation-profiles`),
  qualityGates: () => request<{ total: number; items: QualityGatePolicyVersion[]; can_manage?: boolean; system_default_id?: string }>(`${PLATFORM_BASE}/quality-gates`),
  runComparisons: () => request<{ total: number; items: RunComparison[] }>(`${PLATFORM_BASE}/run-comparisons`),
  createRunComparison: (baselineRunId: string, candidateRunId: string) =>
    request<RunComparison>(`${PLATFORM_BASE}/run-comparisons`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        baseline_run_id: baselineRunId,
        candidate_run_id: candidateRunId,
      }),
    }),
  createEvaluationProfile: (body: {
    languages?: string[];
    quality_gate_policy_version_id?: string;
    status?: 'active'|'disabled';
    project_id?: string;
    profile_key: string;
    version: string;
    display_name: string;
    description: string;
    metrics: Array<{ metric_version_id: string; required: boolean; config: Record<string, unknown> }>;
  }) => request<EvaluationProfileVersion>(`${PLATFORM_BASE}/evaluation-profiles`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }),
  createQualityGate: (body: {
    project_id?: string;
    policy_key: string;
    version: string;
    display_name: string;
    description: string;
    rules: Array<Record<string, unknown>>;
  }) => request<QualityGatePolicyVersion>(`${PLATFORM_BASE}/quality-gates`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }),
  suites: async (): Promise<TestSuite[]> => [
    { key: "yumemi_tts", label: "Yumemi 公网 TTS", enabled: true, speed_min: 1, speed_max: 1, speed_step: 1 },
    { key: "vuilabs_tts", label: "VUILabs 公网 TTS", enabled: true, speed_min: 0.5, speed_max: 2, speed_step: 0.05 },
  ],
  runs: async (): Promise<Run[]> => {
    const page = await request<Page<PlatformRun>>(`${PLATFORM_BASE}/runs?limit=500`);
    return page.items.map(mapRun);
  },
  runProgress: (runId: string) =>
    request<RunProgressSnapshot>(`${PLATFORM_BASE}/runs/${encodeURIComponent(runId)}/progress`),
  runTasks: (runId: string) =>
    request<Page<RunTaskRecord>>(`${PLATFORM_BASE}/runs/${encodeURIComponent(runId)}/tasks?limit=500`),
  runMetrics: (runId: string, summaryOnly = false, attemptScope: "all_attempts" | "latest_attempt" = "latest_attempt") => request<RunMetricResponse>(
    `${PLATFORM_BASE}/runs/${encodeURIComponent(runId)}/metrics${summaryOnly ? `?summary_only=true&attempt_scope=${attemptScope}` : ""}`,
  ),
  runQualityGates: (runId: string, summaryOnly = false) => request<RunQualityGateResponse>(
    `${PLATFORM_BASE}/runs/${encodeURIComponent(runId)}/quality-gates${summaryOnly?"?summary_only=true&attempt_scope=all_attempts":""}`,
  ),
  run: async (runId: string): Promise<RunDetail> => {
    const [rawRun, progress] = await Promise.all([
      request<PlatformRun>(`${PLATFORM_BASE}/runs/${encodeURIComponent(runId)}/overview`),
      request<RunProgressSnapshot>(`${PLATFORM_BASE}/runs/${encodeURIComponent(runId)}/progress`),
    ]);
    const run = mapRun(rawRun);
    run.status = progress.status;
    run.version = progress.version;
    run.summary = summaryFromProgress(progress);
    return {
      run,
      summary: run.summary,
      progress,
      log: [],
      alive: !["completed", "failed", "cancelled", "stopped", "blocked_billing"].includes(progress.status),
    };
  },
  plan: async (body: PlanRequest) => {
    const datasets = await api.datasets();
    const version = datasets.find((item) => item.id === body.dataset_id);
    if (!version?.dataset_id) throw new Error("DatasetVersion 不存在或未绑定逻辑数据集");
    const casePage = await api.datasetCases(version.dataset_id, version.id);
    let catalog: VoiceCatalogResponse;
    try {
      catalog = await api.voices(body.provider);
    } catch (error) {
      if (body.voice_catalog_version !== "manual") throw error;
      catalog = {
        provider: body.provider,
        voices: body.voice_ids.map((voice_id) => ({ voice_id, display_name: voice_id, language: null, status: "manual" })),
        catalog_version: "manual",
        fetched_at: body.voice_catalog_fetched_at,
        age_seconds: 0,
        cache_ttl_seconds: 0,
        fresh_for_seconds: 0,
        cache_source: "memory",
        stale: false,
        refreshing: false,
        warning: "Provider 声线目录不可用，使用人工确认的 voice_id 快照",
      };
    }
    if (casePage.total !== casePage.items.length) throw new Error("当前数据集超过同步计划上限，请拆分 DatasetVersion");
    const [profileCatalog,gateCatalog]=await Promise.all([api.evaluationProfiles(),api.qualityGates()]);
    const languages=casePage.items.filter(c=>c.enabled).map(c=>c.language);
    const selection=resolveEvaluationSelection(profileCatalog.items,gateCatalog.items,body.evaluation_profile_version_id,languages,body.selected_optional_metric_keys);
    if(selection.gate.id!==body.quality_gate_policy_version_id)throw new Error('方案质量要求已变化，请返回重新确认');
    if(selection.asr&&body.audio_format!=='wav')throw new Error('ASR 指标需要 WAV 音频，请重新核对运行参数');
    const selectedVoices = catalog.voices.filter((voice: Voice) => body.voice_ids.includes(voice.voice_id));
    if (selectedVoices.length !== body.voice_ids.length) throw new Error("声线目录已变化，请刷新后重新选择");
    const tasks = expandPlatformTasks(body, casePage.items);
    const response = await request<{ run: PlatformRun; task_count: number }>(`${PLATFORM_BASE}/runs/public/plans`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        run_id: body.run_id,
        dataset_version_id: body.dataset_id,
        provider: body.provider,
        config: {
          ...(body.luna?{luna:body.luna}:{}),
          concurrency: body.concurrency,
          audio_format: body.audio_format,
          assignment_mode: body.assignment_mode === "cartesian" ? "cartesian" : body.assignment_mode === "paired" ? "paired" : "explicit",
          request_namespace: body.run_id,
          detector_enabled: true,
          mos_enabled: selection.mos,
          asr_enabled: selection.asr,
          selected_optional_metric_keys: selection.optionalKeys,
          evaluation_profile_version_id: body.evaluation_profile_version_id,
          quality_gate_policy_version_id: body.quality_gate_policy_version_id,
          metadata: { run_name: body.run_name, priority: body.priority },
        },
        tasks,
        voice_catalog_snapshot: {
          provider: body.provider,
          captured_at: catalog.fetched_at,
          catalog_version: catalog.catalog_version,
          voices: selectedVoices.map(({model_id,source,...voice})=>({...voice,metadata:{model_id,source}})),
          metadata: { stale: catalog.stale },
        },
      }),
    });
    const run = mapRun(response.run);
    return { run, summary: run.summary };
  },
  approveRun: async (run: Run, selfApprovalConfirmed = false) => {
    if (!run.version || !run.plan_sha256 || !run.planned_requests) throw new Error("Run 审批事实不完整");
    const response = await request<{ run: PlatformRun }>(`${PLATFORM_BASE}/runs/${encodeURIComponent(run.id)}/approval`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        expected_version: run.version,
        expected_plan_sha256: run.plan_sha256,
        expected_planned_requests: run.planned_requests,
        admin_self_approval_confirmed: selfApprovalConfirmed,
        project_self_approval_confirmed: selfApprovalConfirmed,
      }),
    });
    return mapRun(response.run);
  },
  settlementAttention: (runId: string, offset = 0) => request<{ total: number; items: { job_id: string; attempt_id: string; task_id: string; error_code: string }[] }>(`${PLATFORM_BASE}/runs/${encodeURIComponent(runId)}/settlement-attention?limit=20&offset=${offset}`),
  retrySettlement: async (run: Run, jobId: string) => {
    if (!run.version) throw new Error("Run 版本不可用");
    const response = await request<{ run: PlatformRun; job_id: string; created: boolean }>(`${PLATFORM_BASE}/runs/${encodeURIComponent(run.id)}/retry-settlement`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ expected_version: run.version, settlement_job_id: jobId }),
    });
    return mapRun(response.run);
  },
  generationAttention: (id: string, offset=0) => request<{total:number;supported:boolean;items:Array<{id:string;task_id:string;error_code:string;external_request_id:string}>}>(`${PLATFORM_BASE}/runs/${encodeURIComponent(id)}/generation-attention?offset=${offset}`),
  asrRecovery: (id: string, offset=0) => request<{total:number;covered:number;unresolved:number;items:Array<{job_id:string;task_id:string;error_code:string;success_run_id:string|null;model:string|null;same_expected_text:boolean|null}>}>(`${PLATFORM_BASE}/runs/${encodeURIComponent(id)}/asr-recovery?offset=${offset}`),
  verifyNotGenerated: (id:string, version:number, attempt_id:string, evidence:string) => request(`${PLATFORM_BASE}/runs/${encodeURIComponent(id)}/verify-not-generated`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({expected_version:version,attempt_id,evidence})}),
  resumeReference: (id:string, version:number) => request(`${PLATFORM_BASE}/runs/${encodeURIComponent(id)}/resume-reference-generation`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({expected_version:version})}),
  referenceBatchPreview: (id:string) => request<{supported:boolean;reason?:string;tasks?:number;references?:number}>(`${PLATFORM_BASE}/runs/${encodeURIComponent(id)}/reference-batch-preview`),
  createReferenceBatch: (id:string, request_key:string) => request<{run_id:string}>(`${PLATFORM_BASE}/runs/${encodeURIComponent(id)}/reference-batches`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({request_key})}),
  resumeGeneration: async (run: Run) => {
    if (!run.version) throw new Error("Run 版本不可用");
    const response = await request<{ run: PlatformRun; pending_generation: number }>(`${PLATFORM_BASE}/runs/${encodeURIComponent(run.id)}/resume-generation`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ expected_version: run.version, reason_code: "operator_request" }),
    });
    return mapRun(response.run);
  },
  stopRun: async (run: Run) => {
    if (!run.version) throw new Error("Run 版本不可用");
    const action = ["draft", "planned", "approved"].includes(run.status) ? "cancel" : "stop";
    const response = await request<{ run: PlatformRun }>(`${PLATFORM_BASE}/runs/${encodeURIComponent(run.id)}/${action}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ expected_version: run.version, reason_code: "operator_request" }),
    });
    return mapRun(response.run);
  },
  runAction: async (runId: string, action: "start" | "stop") => {
    const detail = await api.run(runId);
    const run = action === "start" ? await api.approveRun(detail.run) : await api.stopRun(detail.run);
    return { status: run.status };
  },
  analysis: (filters: { runId?: string; provider?: string; datasetId?: string; dateFrom?: string; dateTo?: string; page?: number; pageSize?: number } = {}, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    if (filters.runId) query.set("run_id", filters.runId);
    if (filters.provider) query.set("provider", filters.provider);
    if (filters.datasetId) query.set("dataset_id", filters.datasetId);
    if (filters.dateFrom) query.set("date_from", filters.dateFrom);
    if (filters.dateTo) query.set("date_to", filters.dateTo);
    query.set("limit", String(filters.pageSize ?? 10));
    query.set("offset", String(((filters.page ?? 1) - 1) * (filters.pageSize ?? 10)));
    return request<AnalysisResponse>(`${PLATFORM_BASE}/analysis-summary?${query}`, { signal });
  },
  candidates: (runId: string) =>
    request<GroupedResponse<Candidate>>(`/api/runs/${encodeURIComponent(runId)}/candidates?limit=1000`),
  review: (runId: string, taskId: string, body: Record<string, unknown>) =>
    request(`/api/runs/${encodeURIComponent(runId)}/reviews/${encodeURIComponent(taskId)}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    }),
  reviewQueueStats: (filters: ReviewAudioFilters = {}, signal?:AbortSignal) => request<PlatformReviewQueueStats>(`${PLATFORM_BASE}/reviews/queue/stats?${caseQuery(filters)}&group_by=audio&include_reassessment=false`,{signal}),
  reviewAudioGroups: (filters: ReviewAudioFilters = {}, offset = 0, signal?:AbortSignal) => request<Page<ReviewAudioGroup>>(`${PLATFORM_BASE}/reviews/audio-groups?${caseQuery(filters, { offset, limit: 30 })}`,{signal}),
  reviewCandidates: (filters: CaseFilters = {}, offset = 0) => request<Page<ReviewFindingSummary>>(`${PLATFORM_BASE}/reviews/candidates?${caseQuery(filters, { offset, limit: 30 })}&include_context=true`),
  reviewCandidate: (candidateId: string) => request<PlatformReviewCandidate>(`${PLATFORM_BASE}/reviews/candidates/${encodeURIComponent(candidateId)}?include_context=true`),
  submitAudioReview: (candidateId: string, body: {
    expected_snapshot: string; decisions: Array<{ candidate_id: string; status: "Confirmed" | "False_Positive"; issue_time: string }>;
    comment: string; severity: string; root_cause: string;
  }) => request<PlatformReviewCandidate>(`${PLATFORM_BASE}/reviews/audio-reviews/${encodeURIComponent(candidateId)}/decisions`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  }),
  claimReview: (candidateId: string) => request<PlatformReviewClaim>(`${PLATFORM_BASE}/reviews/candidates/${encodeURIComponent(candidateId)}/claim`, { method: "POST" }),
  claimNextReview: (filters: ReviewAudioFilters = {}) =>
    request<PlatformReviewClaim | undefined>(`${PLATFORM_BASE}/reviews/queue/claim-next`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(filters),
    }),
  submitReviewDecision: (
    candidateId: string,
    leaseToken: string,
    body: Record<string, unknown>,
  ) => request(`${PLATFORM_BASE}/reviews/candidates/${encodeURIComponent(candidateId)}/decisions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...body, lease_token: leaseToken }),
  }),
  releaseReviewLease: (candidateId: string, leaseToken: string) =>
    request(`${PLATFORM_BASE}/reviews/candidates/${encodeURIComponent(candidateId)}/lease/release`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ lease_token: leaseToken }),
    }),
  referenceBoundaryCapabilities: () => request<ReferenceBoundaryCapabilities>(`${PLATFORM_BASE}/reference-boundary/capabilities`),
  referenceBoundaryCatalog: () => request<ReferenceBoundaryCatalog>(`${PLATFORM_BASE}/reference-boundary/catalog`),
  admitReferenceBoundaryDeployment: (deploymentId: string, manifestSha256: string) =>
    request<ReferenceBoundaryAdmission>(`${PLATFORM_BASE}/reference-boundary/deployments/admit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        deployment_id: deploymentId,
        manifest_sha256: manifestSha256,
      }),
    }),
  referenceBoundaryDryRun: (body: ReferenceBoundaryDryRunRequest) =>
    request<ReferenceBoundaryDryRunResponse>(`${PLATFORM_BASE}/reference-boundary/dry-run`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    }),
  registerReferenceBoundaryRun: async (
    body: ReferenceBoundaryDryRunRequest,
    preview: ReferenceBoundaryDryRunResponse,
  ) => {
    const response = await request<{ run: PlatformRun }>(`${PLATFORM_BASE}/reference-boundary/runs/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...body,
        expected_preview_plan_sha256: preview.plan_sha256,
        expected_deployment_id: preview.deployment_id,
        expected_manifest_sha256: preview.manifest_sha256,
      }),
    });
    return mapRun(response.run);
  },
  executeReferenceBoundaryRun: async (run: Run) => {
    if (!run.version || !run.plan_sha256 || !run.planned_requests) throw new Error("V63 Run 执行事实不完整");
    const response = await request<{ run: PlatformRun }>(
      `${PLATFORM_BASE}/reference-boundary/runs/${encodeURIComponent(run.id)}/execute`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          expected_version: run.version,
          expected_plan_sha256: run.plan_sha256,
          expected_planned_requests: run.planned_requests,
        }),
      },
    );
    return mapRun(response.run);
  },
  badCaseAudioGroups: (filters: CaseFilters = {}, offset = 0,signal?:AbortSignal) => request<BadCaseAudioPage>(`${PLATFORM_BASE}/bad-case-audio-groups?${caseQuery(filters, { offset, limit: 12 })}&include_facets=false`,{signal}),
  badCaseFacets: (filters:CaseFilters={},signal?:AbortSignal)=>request<Pick<BadCaseAudioPage,'category_facets'|'label_facets'>>(`${PLATFORM_BASE}/bad-case-audio-groups?${caseQuery(filters)}&facets_only=true`,{signal}),
  badCases: (filters: CaseFilters = {}, offset = 0) => request<Page<PlatformBadCase> & { severity_counts: Record<string, number> }>(`${PLATFORM_BASE}/bad-cases?${caseQuery(filters, { offset, limit: 30 })}&include_context=true`),
  badCase: (badCaseId: string) => request<PlatformBadCaseDetail>(`${PLATFORM_BASE}/bad-cases/${encodeURIComponent(badCaseId)}?include_context=true`),
  regressionSets: () => request<Page<RegressionSet>>(`${PLATFORM_BASE}/regression-sets?limit=200`),
  createRegressionSet: (body: { key: string; name: string; description: string; project_id?:string }) =>
    request<RegressionSet>(`${PLATFORM_BASE}/regression-sets`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    }),
  regressionSetVersions: (setId: string) =>
    request<Page<RegressionSetVersion>>(`${PLATFORM_BASE}/regression-sets/${encodeURIComponent(setId)}/versions?limit=200`),
  regressionSetItems: (setId: string, versionId: string, offset = 0) => request<Page<{ id: string; bad_case_id: string; definition: Record<string, unknown> }>>(
    `${PLATFORM_BASE}/regression-sets/${encodeURIComponent(setId)}/versions/${encodeURIComponent(versionId)}/items?limit=30&offset=${offset}`,
  ),
  createRegressionSetVersion: (setId: string, body: { version_label: string; bad_case_ids: string[]; parent_version_id?: string | null }) =>
    request<RegressionSetVersion>(`${PLATFORM_BASE}/regression-sets/${encodeURIComponent(setId)}/versions`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    }),
};

export function subscribeRunProgress(
  runId: string,
  onSnapshot: (snapshot: RunProgressSnapshot) => void,
  onRunEvent?: (event: { event_type: string; created_at: string }) => void,
) {
  const source = new EventSource(`${API_BASE}${PLATFORM_BASE}/runs/${encodeURIComponent(runId)}/progress/stream`);
  source.addEventListener("snapshot", (event) => onSnapshot(JSON.parse((event as MessageEvent).data) as RunProgressSnapshot));
  source.addEventListener("run_event", (event) => onRunEvent?.(JSON.parse((event as MessageEvent).data) as { event_type: string; created_at: string }));
  source.addEventListener("terminal", () => source.close());
  return () => source.close();
}

function datasetImportError(error:unknown):never {
 if(error instanceof ApiError){let message=error.message;try{const detail=JSON.parse(message).detail;if(typeof detail==='string')message=detail;}catch{/* Plain messages are already readable. */}
 throw new ApiError(message,error.status);}
 throw error;
}
